import { useEffect, useRef, useState } from 'react'
import { TableComponent } from '../../../components/table'
import { useFetchListCategoriesQuery } from '../../../redux-toolkits/lists/list.slice'

import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext } from '@dnd-kit/sortable'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../../../components/button'
import CountCard from '../../../components/cards/CountCard'
import { Status } from '../../../components/cards/statusTag'
import Icon from '../../../components/icon'
import Dropdown from '../../../components/inputs/dropdown'
import { SearchInput } from '../../../components/inputs/searchInput'
import { AddCategoryModal } from '../../../components/modal/app_modals/addCategoryModal'
import { AddSubCategoryModal } from '../../../components/modal/app_modals/addSubCategoryModal'
import { getCategorySectionLabel } from '../../../components/product/category-section'
import { PageHeader } from '../../../components/typography/PageHeader'
import { CountCardContainer } from '../../../components/wrapper/CountCardsContainer'
import {
  deepCopy,
  getPageFromSkip,
  getSkipFromPage,
  moveElement,
} from '../../../helpers'
import { useDebounceSearch } from '../../../hooks/useDeboucedSearch'
import { useModalControl } from '../../../hooks/useModalControl'
import { ListCategoryDoc } from '../../../redux-toolkits/categories/categories.type'
import { useReorderCategoriesMutation } from '../../../redux-toolkits/products/products.slice'

export const AllCategories = ({
  onCategoryClick,
}: {
  onCategoryClick: (cat: ListCategoryDoc) => void
}) => {
  const [query, setQuery] = useState({
    skip: 0,
    limit: 1000,
  })
  const [search, setSearch] = useState<string>()
  const [categories, setCategories] = useState<ListCategoryDoc[]>([])
  const [selectedCategory, setSelectedCategory] = useState<ListCategoryDoc>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { setModalOpen, modalOpen, handleModalOpen, handleModalClose, modal } =
    useModalControl()

  const { data, isLoading: loadingCategories } = useFetchListCategoriesQuery(
    {
      skip: query.skip,
      limit: query.limit,
      search: search,
      isAdmin: true,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const [reorderCategories] = useReorderCategoriesMutation()
  const isUserReorder = useRef(false)

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e

    if (over && active.id !== over.id) {
      const dataCopy: ListCategoryDoc[] = deepCopy(categories)
      const dataCopyMod = dataCopy.map((subCat) => {
        return {
          id: `${subCat.name}-${subCat._id}`,
          ...subCat,
        }
      })

      const oldIndex = dataCopyMod.findIndex((item) => item.id === active.id)
      const newIndex = dataCopyMod.findIndex((item) => item.id === over.id)

      const reorderedCategories = moveElement(dataCopy, oldIndex, newIndex)

      isUserReorder.current = true
      setCategories(reorderedCategories)
    }
  }

  useDebounceSearch({
    searchQuery: categories,
    onSearch: async () => {
      if (!isUserReorder.current) return

      isUserReorder.current = false

      await reorderCategories({
        categoryIds: categories.map((cat) => cat._id),
      })
    },
    delay: 2000,
  })

  useEffect(() => {
    if (data?.data?.data) {
      const sortedCategories = [...data.data.data]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .sort((a, b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0))

      setCategories(sortedCategories)
    }
  }, [data])

  return (
    <div>
      <PageHeader title="Categories" />

      <div className="w-full gap-4 flex flex-wrap items-center justify-between">
        <div className="!w-full md:!max-w-[400px]">
          <SearchInput
            searchFunction={(search) => {
              setSearch(search === '' ? undefined : search)
              setQuery({ ...query, skip: 0 })
            }}
            placeholder="Search categories"
          />
        </div>

        <Button
          size="medium"
          label="Add Category"
          primary
          onClick={() => {
            handleModalOpen('addCategoryModal')
          }}
        />
      </div>

      <CountCardContainer>
        <CountCard
          isCurrency={false}
          text="Total categories count"
          count={data?.data.totalItems ?? 0}
        />
      </CountCardContainer>

      <DndContext
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={
            categories.map((cat) => {
              return {
                id: `${cat.name}-${cat._id}`,
                ...cat,
              }
            }) ?? []
          }
        >
          <div className="w-full overflow-x-scroll">
            <TableComponent
              headers={['Name', 'Section', 'Status', 'Actions']}
              rows={
                categories.length
                  ? categories.map((singleCategory) => {
                      return {
                        id: `${singleCategory.name}-${singleCategory._id}`,
                        content: [
                          <button
                            key={`${singleCategory._id}-name`}
                            className="w-full text-left hover:underline"
                            onClick={(): any => {
                              onCategoryClick(singleCategory)
                            }}
                          >
                            {singleCategory.name}
                          </button>,
                          <span key={`${singleCategory._id}-section`}>
                            {getCategorySectionLabel(singleCategory.section)}
                          </span>,
                          <Status
                            key={`${singleCategory._id}-status`}
                            text={
                              singleCategory.isActive ? 'Active' : 'Inactive'
                            }
                            type={singleCategory.isActive ? 'success' : 'fail'}
                          />,
                          <Dropdown
                            key={`${singleCategory._id}-controls`}
                            menuButton={
                              <Icon
                                id="ellipses"
                                height={18}
                                width={18}
                                className=""
                              />
                            }
                            menuItems={[
                              {
                                name: 'Add Subcategory',
                                value: 'add-subcategory',
                                onClick: () => {
                                  handleModalOpen('addSubCategoryModal')
                                  setSelectedCategory(singleCategory)
                                },
                              },
                            ]}
                          />,
                        ],
                      }
                    })
                  : []
              }
              name="categories-table"
              loading={loadingCategories}
              isEmpty={[undefined, 0].includes(data?.data?.data.length)}
              currentPage={getPageFromSkip(query.skip, query.limit)}
              totalDataCount={data?.data.totalItems ?? 0}
              pageLimit={query.limit}
              onPageChange={(number) => {
                setQuery({
                  ...query,
                  skip: getSkipFromPage(number, query.limit),
                })
              }}
              sortable
            />
          </div>
        </SortableContext>
      </DndContext>

      <AddCategoryModal
        open={modalOpen && modal === 'addCategoryModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedCategory(undefined)
        }}
        onSuccessCallback={() => {
          setSelectedCategory(undefined)
        }}
        mode={'create'}
        category={selectedCategory}
      />

      {selectedCategory ? (
        <AddSubCategoryModal
          open={modalOpen && modal === 'addSubCategoryModal'}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            handleModalClose()
            setSelectedCategory(undefined)
          }}
          onSuccessCallback={(subCat) => {
            handleModalClose()
            setSearchParams({
              ...Object.fromEntries(searchParams.entries()),
              view: 'subcategory',
              category: subCat.category._id,
              subcategory: subCat._id,
            })
          }}
          mode="create"
          categoryId={selectedCategory?._id}
        />
      ) : undefined}
    </div>
  )
}
